<script lang="ts">
  import { ArrowDown, ArrowUp } from "lucide-svelte";
  import type { Snippet } from "svelte";
  import { innerWidth } from "svelte/reactivity/window";
  import { slide } from "svelte/transition";
  import type { View, VisualizationSpec } from "svelte-vega";

  import CodeSample from "./CodeSample.svelte";
  import FiltersContainer from "./FiltersContainer.svelte";
  import LLMFilters from "./LLMFilters.svelte";
  import SimpleSelect from "./SimpleSelect.svelte";
  import VegaWrapper from "./VegaWrapper.svelte";
  import { processRows } from "./process-rows";
  import type { ExtendedRow } from "./process-rows";
  import {
    columnGroups,
    displayColumns,
    yMetricAggregates,
    yMetricGroups,
  } from "./shared";
  import { type Column, type FilterCondition, type Row } from "./types";

  interface Props {
    rows: Row[] | undefined;
    loadingError: Error | undefined;
    clientWidth: number;
    hideFilters?: boolean;
    hideCodeSample?: boolean;
    skipBestCalculation?: boolean;
    hideLegend?: boolean;
    filterConditions: FilterCondition[];
    selectedYMetricGroupKey: "ttft" | "itl" | "ttlt";
    selectedYMetricAggregateKey: "mean" | "p50" | "p90" | "p95";
    selectedBestBy?: "throughput" | "latency" | "all";
    setFilterConditions?: (filterConditions: FilterCondition[]) => void;
    useOpacity?: boolean;
    curious?: Snippet;
  }

  let {
    rows,
    loadingError,
    clientWidth,
    hideFilters = false,
    hideCodeSample = false,
    skipBestCalculation = false,
    hideLegend = false,
    filterConditions = $bindable(),
    selectedYMetricGroupKey = $bindable(),
    selectedYMetricAggregateKey = $bindable(),
    selectedBestBy = $bindable("all"),
    setFilterConditions,
    useOpacity = false,
    curious,
  }: Props = $props();

  let processedRows = $derived(
    processRows(
      rows ?? [],
      filterConditions,
      skipBestCalculation
        ? undefined
        : `${selectedYMetricGroupKey}_${selectedYMetricAggregateKey}`,
    ),
  );
  let filteredRows = $derived(processedRows.filteredRows);
  let filteredData = $derived({ rows: filteredRows });
  let discriminativeColumns = $derived(processedRows.discriminativeColumns);
  let colorScale = $derived(processedRows.colorScale);

  let isMobile = $derived((innerWidth.current ?? 1000) < 1024);
  let width = $derived(
    isMobile || hideCodeSample
      ? clientWidth
      : Math.min(
          clientWidth,
          // 300 is the width of the code sample, plus padding
          clientWidth / 2 + (innerWidth.current ?? 1000) / 2 - 350,
        ),
  );
  let height = $derived(Math.max(600, width * 0.5));

  const spec = $derived<VisualizationSpec>({
    $schema: "https://vega.github.io/schema/vega-lite/v6.json",
    description:
      "A line chart of different LLM engine benchmark runs, in different configurations.",
    height,
    width,
    autosize: {
      type: "fit",
      resize: true,
    },
    data: {
      name: "rows",
    },
    params: [
      {
        name: "yField",
        value: "ttft_p95",
      },
      {
        name: "yTitle",
        value: ["← Lower is better", "Median time to first token (s)"],
      },
      {
        name: "highlight",
        select: {
          type: "point",
          on: "pointerover",
          fields: ["fullDescription"],
        },
      },
      {
        name: "clicked",
        select: {
          type: "point",
          on: "click",
          fields: ["fullDescription"],
          toggle: false,
        },
      },
    ],
    transform: [
      {
        // itl - inter-token latency
        calculate: "datum[yField] * (yField[0] === 'i' ? 1000 : 1)",
        as: "selectedYMetric",
      },
      {
        joinaggregate: [
          { op: "argmax", field: "queries_per_second", as: "maxThroughput" },
        ],
      },
      {
        calculate:
          "datum['maxThroughput']['fullDescription'] === datum['fullDescription']",
        as: "isMaxThroughput",
      },
      ...(selectedBestBy === "throughput"
        ? [
            {
              filter: "datum['isMaxThroughput']",
            },
          ]
        : []),
      {
        joinaggregate: [
          { op: "argmin", field: "interpoloated_metric", as: "minMetricDatum" },
        ],
      },
      {
        calculate:
          "datum['minMetricDatum']['fullDescription'] === datum['fullDescription']",
        as: "isMinMetricDatum",
      },
      ...(selectedBestBy === "latency"
        ? [
            {
              filter: "datum['isMinMetricDatum']",
            },
          ]
        : []),
    ],
    mark: {
      type: "line",
      point: {
        size: useOpacity ? 40 : 60,
      },
      opacity: useOpacity ? 0.7 : 1,
      tooltip: { content: "data" },
      strokeWidth: useOpacity ? 2 : 3,
      strokeCap: "round",
    },
    encoding: {
      x: {
        field: "queries_per_second",
        type: "quantitative",
        title: ["Throughput (requests/s per replica)", "Higher is better →"],
        axis: {
          labelOverlap: "parity",
          labelSeparation: 25,
          titlePadding: 20,
        },
      },
      y: {
        field: "selectedYMetric",
        type: "quantitative",
        title: { signal: "yTitle" },
        scale: {
          type: "log",
          base: 10,
        },
      },
      color: {
        field: "legendString",
        type: "nominal",
        title: null,
        scale: colorScale,
        ...(hideLegend && { legend: null }),
      },
      detail: {
        field: "fullDescription",
        type: "nominal",
        ...(hideLegend && { legend: null }),
      },
      ...(useOpacity && {
        opacity: {
          condition: [
            {
              param: "highlight",
              value: 1,
              empty: false,
            },
          ],
          value: 0.4,
        },
      }),
      tooltip: [
        ...discriminativeColumns.map((c) => ({
          field: c.label,
          type: "nominal" as const,
          title: c.label,
        })),
        {
          field: "selectedYMetric",
          type: "quantitative" as const,
          title: "Latency (s)",
          format: "5.3f",
        },
        {
          field: "queries_per_second",
          type: "quantitative" as const,
          title: "Throughput (req/s)",
          format: ".2f",
        },
      ],
    },
  });

  let viewVL: View | undefined = $state();

  // Create ordered list of columns
  const orderedColumns = [
    ...columnGroups.configuration,
    // ...columnGroups.metrics,
  ] as const;

  const distinctTableRows = $derived(
    Object.values(
      Object.fromEntries(
        filteredRows.map((r) => [
          JSON.stringify(orderedColumns.map((c) => [c, r[c]])),
          r,
        ]),
      ),
    ),
  );

  // Function to format metric values
  function formatMetricValue(
    column: Column,
    value: string | number | null,
  ): string {
    if (value === null) {
      return "–";
    }
    return `${value}`;
  }

  let selectedYMetricGroup = $derived(
    yMetricGroups.find((g) => g.key === selectedYMetricGroupKey) ||
      yMetricGroups[0],
  );
  let selectedYMetricAggregate = $derived(
    yMetricAggregates.find((a) => a.key === selectedYMetricAggregateKey) ||
      yMetricAggregates[0],
  );

  let highestThroughputRow = $state<ExtendedRow>();
  let clickedFullDescription = $state<string>();
  let exampleClickedRow = $derived.by(() => {
    if (!clickedFullDescription) {
      return highestThroughputRow;
    }
    return filteredRows.find(
      (r) => r.fullDescription === clickedFullDescription,
    );
  });

  $effect(() => {
    if (viewVL) {
      const newField = `${selectedYMetricGroupKey}_${selectedYMetricAggregateKey}`;
      const measure = selectedYMetricGroupKey === "itl" ? "ms" : "s";
      const newTitle = [
        `Latency (${measure}, ${selectedYMetricAggregate.label} ${selectedYMetricGroup.label})`,
        "← Lower is better",
      ];

      viewVL.signal("yField", newField).signal("yTitle", newTitle).run();

      // Add listener for clicked signal
      const listener = (
        _name: string,
        value: { fullDescription?: string[] },
      ) => {
        clickedFullDescription = value.fullDescription?.[0];
      };
      viewVL.addSignalListener("clicked", listener);

      return () => {
        viewVL?.removeSignalListener("clicked", listener);
      };
    }
  });

  $effect(() => {
    if (viewVL) {
      spec;

      // Get data from the transformed dataset (to avoid filtered out rows)
      highestThroughputRow = viewVL
        .data("data_1")
        .sort((a, b) => b.queries_per_second - a.queries_per_second)[0];
      console.log(viewVL.data("data_1"));
    }
  });

  let showData = $state(false);
  let showCode = $state(false);

  export function setRandomFilterConditions() {
    if (!rows) {
      return;
    }
    // Make sure each configuration has an equal chance of being selected
    const groupedRows = Object.groupBy(rows, (r) =>
      JSON.stringify(displayColumns.map((c) => c.value(r))),
    );
    const keys = Object.keys(groupedRows);
    const randomGroup =
      groupedRows[keys[Math.floor(Math.random() * keys.length)]];
    if (!randomGroup) {
      return;
    }
    const randomRow = randomGroup[0];
    const filterConditions: FilterCondition[] = [];
    filterConditions.push({
      // reset the latency bound to a value hit by all configurations
      type: "range" as const,
      column: "ttft_p95",
      maxValue: Number("1000") / 1000,
    });
    // pick random row, extract the filter conditions from it
    for (const [key, value] of Object.entries(randomRow)) {
      if (["model", "tokens"].includes(key)) {
        filterConditions.push({
          type: "eq",
          column: key as "model" | "tokens",
          value: `${value}`,
        });
      }
    }
    setFilterConditions?.(filterConditions);
  }
</script>

{#if !hideFilters}
  <LLMFilters {filterConditions} {setFilterConditions}>
    {#snippet extraFilter()}
      {#if setFilterConditions && !hideFilters}
        <FiltersContainer active class="self-stretch">
          <span>I want to see</span>
          <SimpleSelect
            bind:value={selectedBestBy}
            options={[
              { label: "the highest throughput", value: "throughput" },
              { label: "the lowest latency", value: "latency" },
              { label: "every benchmarked", value: "all" },
            ]}
          />
          <span>configuration</span>
        </FiltersContainer>
      {/if}
    {/snippet}
  </LLMFilters>
{/if}

{@render curious?.()}

<div
  class={[
    "relative self-center vega-container max-w-full aspect-[2/1] min-h-[600px] select-none",
    !hideFilters && "mt-8",
  ]}
>
  {#if !viewVL || !rows || loadingError}
    <div
      class={[
        "absolute z-10 inset-0 rounded-md border-4 border-almanac-lime-green/10 text-3xl grid place-items-center text-almanac-dark-green/50",
        !rows && !loadingError && "animate-pulse",
      ]}
    >
      {loadingError ? "Error loading data" : "Loading..."}
    </div>
  {:else if rows.length > 0 && filteredRows.length === 0}
    <img
      src="https://modal-cdn.com/llm-almanac/divider-3.png"
      alt=""
      class="aspect-square h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-5"
    />
    <div
      class="absolute z-10 inset-0 text-3xl grid place-items-center text-almanac-dark-green"
    >
      <p>
        We've never done that.<br />
        <span class="text-xl">
          Think you can?
          <a
            href="https://github.com/modal-labs/stopwatch"
            target="_blank"
            class="text-almanac-dark-green underline underline-offset-[3px] decoration-[1px] hover:text-almanac-dark-green transition-colors"
          >
            Show us how!
          </a>
        </span>
      </p>
    </div>
  {/if}
  <div
    class={[rows && rows.length > 0 && filteredRows.length === 0 && "hidden"]}
  >
    <VegaWrapper
      data={filteredData}
      {spec}
      bind:viewVL
      {isMobile}
      bottomLegend={true}
    />
  </div>
  {#if exampleClickedRow && !hideCodeSample && !isMobile}
    <div
      class={[
        "group lg:absolute lg:left-1/2 lg:top-0 lg:bottom-0 lg:w-screen lg:translate-x-[-50%] overflow-hidden pointer-events-none",
        "relative",
      ]}
    >
      <CodeSample
        row={exampleClickedRow}
        class={[
          "absolute right-0 top-8 bottom-6 translate-x-[calc(100%-300px)] group-hover:translate-x-[-10px] pointer-events-auto",
          "overflow-auto w-[calc(min(800px,100%-100px))] max-h-full select-text",
        ]}
      />
    </div>
  {/if}
</div>
<!-- Take the width of the container until we take the width measurement
 (not great, but less annoying visually than setting width to 0) -->
<div
  style:width={width ? `${width}px` : undefined}
  class="grid min-w-0 sm:grid-cols-[minmax(auto,88px)_1fr]"
>
  <div
    class="flex flex-col lg:flex-row gap-4 lg:gap-8 mb-4 mt-8 items-center self-center justify-center sm:col-start-2"
  >
    <div>
      <label for="metric-group" class="mr-1 font-semibold">Metric:</label>
      <SimpleSelect
        bind:value={selectedYMetricGroupKey}
        variant="transparent"
        options={yMetricGroups.map((group) => ({
          label: group.label,
          value: group.key,
        }))}
        id="metric-group"
      />
    </div>

    <div>
      <label for="aggregate" class="mr-1 font-semibold">Aggregate:</label>
      <SimpleSelect
        bind:value={selectedYMetricAggregateKey}
        variant="transparent"
        options={yMetricAggregates.map((agg) => ({
          label: agg.label,
          value: agg.key,
        }))}
        id="aggregate"
      />
    </div>
  </div>
</div>
{#if exampleClickedRow && !hideCodeSample && isMobile}
  <!-- wrapper -->
  <div
    class={["mb-8 overflow-hidden relative", !showCode && "max-h-[150px]"]}
    role="button"
    aria-label={showCode ? "Hide code block" : "Show more code"}
    onclick={() => (showCode = !showCode)}
    tabindex="0"
    onkeydown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        showCode = !showCode;
      }
    }}
  >
    <CodeSample mobile row={exampleClickedRow} />
    {#if !showCode}
      <div
        class="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#fcfcfc] to-transparent pointer-events-none"
      ></div>
    {/if}
  </div>
{/if}

<div class="self-center mt-8 hidden">
  <button
    class="text-sm flex items-center gap-1 rounded-[3px] hover:bg-almanac-lime-green/5 px-2 py-1 transition-colors"
    onclick={() => (showData = !showData)}
  >
    {showData ? "Hide Data" : "Show Data"}
    {#if showData}
      <ArrowUp class="w-4 h-4" />
    {:else}
      <ArrowDown class="w-4 h-4" />
    {/if}
  </button>
</div>

{#if showData}
  <div
    class={[
      "mt-2 -ml-12 -mr-12 lg:ml-12 lg:mr-12 self-center max-w-[calc(100%+96px)] lg:max-w-full text-sm",
      "overflow-x-scroll border-y border-y lg:border-x border-almanac-lime-green/20 lg:rounded-md bg-[#F4F4F4]/60",
    ]}
    transition:slide={{ duration: 200, axis: "y" }}
  >
    <table class="bg-[#F4F4F4]/60">
      <thead class="border-b border-almanac-lime-green/10">
        <tr class="pb-2">
          {#each orderedColumns as column, colIdx}
            <th
              class={[
                "text-left font-bold py-2 px-4 whitespace-nowrap",
                colIdx > 0 ? "pl-6" : "",
              ]}
            >
              <div class="flex items-center gap-2">
                <span>{column}</span>
              </div>
            </th>
          {/each}
        </tr>
      </thead>
      <tbody>
        {#each distinctTableRows as row}
          <tr>
            {#each orderedColumns as column, colIdx}
              <td
                class={[
                  "whitespace-nowrap px-4 pt-1 align-top",
                  colIdx > 0 ? "pl-6" : "",
                  // columnDescriptors[column].mono
                  //   ? "font-mono whitespace-pre text-ellipsis max-w-[300px] overflow-hidden"
                  // : "",
                ]}
              >
                {formatMetricValue(column, row[column])}
              </td>
            {/each}
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}
